import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  FileDown,
  Trash2,
  FileText,
  Mail,
  X,
  CheckCircle,
  Plus,
  CloudCog,
  Info,
  Pencil,
  Check,
  Search,
  Archive,
  Lock,
} from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router';
import { useAuth } from '../hooks/useAuth';
import { useCartActions } from '../hooks/useCartActions';
import { useSettingsStore } from '../stores/useSettingsStore';
import type { CartProduct } from '../hooks/useCarts';
import { useProductCatalog } from '../hooks/useProductCatalog';
import Button from '../components/common/Button';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { Loader2 } from 'lucide-react';
import { useDocumentPdfDownload } from '../hooks/usePdfDownload';
import { useOfflineStatus } from '../hooks/useOfflineStatus';
import { useToast } from '../context/ToastContext';
import EmailDialog from '../components/common/EmailDialog';
import type { EmailDialogSendData } from '../components/common/EmailDialog';
import { useCartEmail, useCartFileTypes } from '../hooks/useCartEmail';
import { useTranslation } from '../i18n';

function formatProductList(products: CartProduct[]): string {
  return products
    .map((p, i) => {
      const partner = p.partner ? ` — ${p.partner.name}` : '';
      return `  ${i + 1}. ${p.name} (${p.articleNumber})${partner}`;
    })
    .join('\n');
}

const MAX_CARTS = Infinity;

const CartPage = () => {
  const { isLoggedIn, user } = useAuth();
  const multiCartEnabled = useSettingsStore((s) => s.multiCartEnabled);
  const {
    removeFromCart,
    clearCart,
    carts,
    activeCartId,
    switchCart,
    createCart,
    archiveCart,
    deleteCart,
    renameCart,
  } = useCartActions(isLoggedIn);
  const navigate = useNavigate();
  const location = useLocation();
  const { t: strings, lang } = useTranslation();
  const documentPdf = useDocumentPdfDownload();
  const cartEmail = useCartEmail();
  const { isOnline } = useOfflineStatus();
  const toaster = useToast();

  // Toast state
  const [toast, setToast] = useState<string | null>(null);

  const showToast = useCallback((message: string) => {
    setToast(message);
  }, []);

  // Auto-dismiss toast after 3 seconds
  useEffect(() => {
    if (toast === null) return;
    const timer = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(timer);
  }, [toast]);

  // Salesforce dialog state
  const [sfDialogOpen, setSfDialogOpen] = useState(false);

  // Email modal state
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);

  // Cart rename state
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  // Cart search overlay state
  const [cartSearchOpen, setCartSearchOpen] = useState(false);
  const [cartSearchQuery, setCartSearchQuery] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    action: 'delete' | 'clear' | 'archive';
    cartId?: number | string;
    cartName?: string;
  } | null>(null);

  const activeCart = carts.find((c) => c.id === activeCartId);
  const lockedAt = activeCart?.lockedAt ?? null;
  const isLocked = lockedAt !== null;
  const lockedNotice =
    lockedAt !== null
      ? strings.cart.lockedNotice(
          new Date(lockedAt).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: 'numeric' }),
        )
      : null;

  // Resolve products from active cart, enriching with catalog data for offline
  const { data: catalog } = useProductCatalog();
  const cartProducts: CartProduct[] = useMemo(() => {
    const raw = activeCart?.products ?? [];
    if (!catalog) return raw;
    return raw.map((p) => {
      if (p.name) return p;
      const full = catalog.find((c) => c.id === p.id);
      return full ? { ...full, pivot: p.pivot } as CartProduct : p;
    });
  }, [activeCart?.products, catalog]);

  const lockedBanner = lockedNotice !== null ? (
    <div
      role="status"
      className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300"
    >
      <Lock className="w-4 h-4 shrink-0 mt-0.5" />
      <span>{lockedNotice}</span>
    </div>
  ) : null;

  const cartFileTypes = useCartFileTypes(activeCart?.id, cartProducts.map((p) => p.id));

  const handleSendEmail = useCallback((data: EmailDialogSendData) => {
    if (!activeCart) return;

    setEmailError(null);
    cartEmail.mutate(
      {
        cartId: activeCart.id,
        products: data.selectedProducts ?? cartProducts,
        recipients: data.recipients,
        subject: data.subject,
        body: data.body,
        includedTypes: data.includedTypes,
        documentTitle: activeCart.name,
        strings,
        contactPerson: user?.name,
        companyName: user?.branch_office?.name,
        companyWebsite: user?.branch_office?.website ?? undefined,
      },
      {
        onSuccess: () => {
          setEmailOpen(false);
          showToast(strings.cart.toastEmail);
        },
        onError: (err) => {
          const apiErr = err as { body?: { errors?: Record<string, string[]> }; message?: string };
          if (apiErr.body?.errors) {
            const firstError = Object.values(apiErr.body.errors).flat()[0];
            setEmailError(firstError ?? strings.cart.email.sendError);
          } else {
            setEmailError(apiErr.message ?? strings.cart.email.sendError);
          }
        },
      },
    );
  }, [activeCart, cartProducts, cartEmail, strings, showToast, user?.name, user?.branch_office?.name, user?.branch_office?.website]);

  const startEditingTitle = () => {
    setTitleDraft(activeCart?.name ?? '');
    setEditingTitle(true);
  };

  const saveTitle = () => {
    const trimmed = titleDraft.trim();
    if (trimmed && activeCart) {
      renameCart(activeCart.id, trimmed);
    }
    setEditingTitle(false);
  };

  const handleConfirmAction = () => {
    if (!confirmDialog) return;
    switch (confirmDialog.action) {
      case 'delete':
        if (confirmDialog.cartId) deleteCart(Number(confirmDialog.cartId));
        break;
      case 'clear':
        clearCart();
        break;
      case 'archive':
        if (confirmDialog.cartId) archiveCart(Number(confirmDialog.cartId));
        break;
    }
    setConfirmDialog(null);
  };

  /* ------------------------------------------------------------------ */
  /* Cart tabs (multi-cart)                                              */
  /* ------------------------------------------------------------------ */
  const filteredSearchCarts = carts.filter((c) =>
    c.name.toLowerCase().includes(cartSearchQuery.toLowerCase())
  );

  const cartTabs = (
    <div className="flex items-center gap-1 flex-wrap">
      {carts.length <= 3 ? (
        carts.map((c) => (
          <button
            key={c.id}
            type="button"
            onClick={() => switchCart(c.id)}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              c.id === activeCartId
                ? 'bg-vivatis-600 text-white'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            <span>{c.name}</span>
            <span className={`text-xs ${c.id === activeCartId ? 'text-white/70' : 'text-gray-400 dark:text-gray-500'}`}>
              {c.products.length}
            </span>
          </button>
        ))
      ) : (
        <button
          type="button"
          onClick={() => { setCartSearchQuery(''); setCartSearchOpen(true); }}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
        >
          <FileDown className="w-3.5 h-3.5" />
          <span>{strings.cart.showDocuments}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{carts.length}</span>
        </button>
      )}
      {carts.length < MAX_CARTS && (
        <button
          type="button"
          onClick={() => createCart(`Document ${carts.length + 1}`)}
          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium text-vivatis-600 dark:text-vivatis-400 bg-vivatis-50 dark:bg-vivatis-950/30 hover:bg-vivatis-100 dark:hover:bg-vivatis-950/50 transition-colors"
        >
          <Plus className="w-3.5 h-3.5" />
          <span>{strings.cart.newDocument}</span>
        </button>
      )}

      {/* Cart search overlay */}
      {cartSearchOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm" onClick={() => setCartSearchOpen(false)} />
          <div className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]">
            <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
              {/* Search input */}
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <Search className="w-5 h-5 text-gray-400 shrink-0" />
                <input
                  type="text"
                  autoFocus
                  value={cartSearchQuery}
                  onChange={(e) => setCartSearchQuery(e.target.value)}
                  placeholder={strings.cart.searchDocuments}
                  className="flex-1 bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 outline-none"
                />
                <button
                  type="button"
                  onClick={() => setCartSearchOpen(false)}
                  className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Results list */}
              <div className="max-h-[50vh] overflow-y-auto py-1">
                {filteredSearchCarts.length === 0 ? (
                  <p className="px-4 py-6 text-sm text-gray-400 text-center">{strings.cart.noDocumentsFound}</p>
                ) : (
                  filteredSearchCarts.map((c) => (
                    <div
                      key={c.id}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
                        c.id === activeCartId
                          ? 'bg-vivatis-50 text-vivatis-700 dark:bg-vivatis-900/30 dark:text-vivatis-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                      }`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          switchCart(c.id);
                          setCartSearchOpen(false);
                        }}
                        className="flex items-center gap-3 flex-1 min-w-0 text-left"
                      >
                        <FileDown className="w-4 h-4 shrink-0 opacity-50" />
                        <span className="flex-1">{c.name}</span>
                        <span className="text-xs text-gray-400 dark:text-gray-500">{c.products.length} {strings.cart.items}</span>
                        {c.id === activeCartId && <Check className="w-4 h-4 text-vivatis-600 dark:text-vivatis-400 shrink-0" />}
                      </button>
                      {carts.length > 1 && (
                        <button
                          type="button"
                          onClick={() => deleteCart(c.id)}
                          className="p-1 rounded-lg text-gray-300 hover:text-red-500 dark:text-gray-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors shrink-0"
                          title={strings.cart.deleteDocument}
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );

  /* ------------------------------------------------------------------ */
  /* Empty state                                                        */
  /* ------------------------------------------------------------------ */
  if (cartProducts.length === 0) {
    return (
      <div className="space-y-8">
        <div>
          <div className="flex items-center gap-3">
            <FileDown className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
            {activeCart ? (
              <div className="flex items-center gap-2">
                {editingTitle ? (
                  <form
                    className="flex items-center gap-2"
                    onSubmit={(e) => { e.preventDefault(); saveTitle(); }}
                  >
                    <input
                      type="text"
                      value={titleDraft}
                      onChange={(e) => setTitleDraft(e.target.value)}
                      className="text-3xl font-bold text-anthracite-500 dark:text-gray-100 bg-transparent border-b-2 border-vivatis-600 focus:outline-none w-64"
                      autoFocus
                      onBlur={saveTitle}
                    />
                    <button type="submit" className="p-1 rounded-lg text-vivatis-600 hover:text-vivatis-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                      <Check className="w-5 h-5" />
                    </button>
                  </form>
                ) : (
                  <>
                    <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
                      {activeCart.name}
                    </h1>
                    {!isLocked && (
                      <button
                        type="button"
                        onClick={startEditingTitle}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-vivatis-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                        title={strings.cart.rename}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    )}
                  </>
                )}
              </div>
            ) : (
              <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
                {strings.cart.title}
              </h1>
            )}
          </div>
          <p className="mt-1 text-gray-500 dark:text-gray-400 ml-10">
            {strings.cart.subtitle}
          </p>
          {multiCartEnabled && cartTabs && <div className="mt-4 ml-10">{cartTabs}</div>}
          {lockedBanner && <div className="mt-4 ml-10">{lockedBanner}</div>}
        </div>

        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FileDown className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
            {strings.cart.emptyTitle}
          </h2>
          <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md">
            {strings.cart.emptyDescription}
          </p>
          <div className="flex items-center gap-3">
            <Link
              to="/products"
              className="inline-flex items-center gap-2 rounded-full bg-vivatis-600 px-5 py-2.5 text-sm font-medium text-white hover:bg-vivatis-700 transition-colors"
            >
              {strings.common.browseProducts}
            </Link>
            {carts.length > 1 && !isLocked && (
              <button
                type="button"
                onClick={() => setConfirmDialog({ action: 'delete', cartId: activeCartId ?? undefined, cartName: activeCart?.name })}
                className="inline-flex items-center gap-2 rounded-full border border-red-200 dark:border-red-800 px-5 py-2.5 text-sm font-medium text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                {strings.cart.deleteDocument}
              </button>
            )}
          </div>
        </div>

        {/* Confirm dialog for destructive cart actions */}
        <ConfirmDialog
          isOpen={confirmDialog !== null}
          onConfirm={handleConfirmAction}
          onCancel={() => setConfirmDialog(null)}
          title={strings.cart.deleteDocument}
          message={strings.cart.confirmDelete(confirmDialog?.cartName ?? '')}
          confirmLabel={strings.cart.deleteDocument}
          variant="danger"
          icon={<Trash2 className="w-6 h-6" />}
        />
      </div>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Filled state                                                       */
  /* ------------------------------------------------------------------ */
  return (
    <div className="space-y-8">
      {/* Toast banner */}
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 flex items-center gap-2 rounded-full bg-green-600 px-5 py-3 text-white shadow-lg animate-fade-in">
          <CheckCircle className="w-5 h-5 shrink-0" />
          <span className="text-sm font-medium">{toast}</span>
          <button
            type="button"
            className="ml-2 p-0.5 rounded hover:bg-green-700 transition-colors"
            onClick={() => setToast(null)}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <FileDown className="w-7 h-7 text-vivatis-600 dark:text-vivatis-400" />
          {activeCart ? (
            <div className="flex items-center gap-2">
              {editingTitle ? (
                <form
                  className="flex items-center gap-2"
                  onSubmit={(e) => { e.preventDefault(); saveTitle(); }}
                >
                  <input
                    type="text"
                    value={titleDraft}
                    onChange={(e) => setTitleDraft(e.target.value)}
                    className="text-3xl font-bold text-anthracite-500 dark:text-gray-100 bg-transparent border-b-2 border-vivatis-600 focus:outline-none w-64"
                    autoFocus
                    onBlur={saveTitle}
                  />
                  <button type="submit" className="p-1 rounded-lg text-vivatis-600 hover:text-vivatis-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer">
                    <Check className="w-5 h-5" />
                  </button>
                </form>
              ) : (
                <>
                  <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
                    {activeCart.name}
                  </h1>
                  {!isLocked && (
                    <button
                      type="button"
                      onClick={startEditingTitle}
                      className="p-1.5 rounded-lg text-gray-400 hover:text-vivatis-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors cursor-pointer"
                      title={strings.cart.rename}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}
            </div>
          ) : (
            <h1 className="text-3xl font-bold text-anthracite-500 dark:text-gray-100">
              Meeting Document
            </h1>
          )}
        </div>
        <p className="mt-1 text-gray-500 dark:text-gray-400 ml-10">
          Products for your customer meeting
        </p>
        {multiCartEnabled && cartTabs && <div className="mt-4 ml-10">{cartTabs}</div>}
        {lockedBanner && <div className="mt-4 ml-10">{lockedBanner}</div>}
      </div>

      {/* Product list */}
      <div className="space-y-2">
        {cartProducts.map((product, index) => (
          <div
            key={product.id}
            role="button"
            tabIndex={0}
            onClick={() => navigate(`/products/${product.id}`, { state: { backgroundLocation: location } })}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                navigate(`/products/${product.id}`, { state: { backgroundLocation: location } });
              }
            }}
            className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800/80 transition-all duration-200 px-4 py-3 group cursor-pointer"
          >
            {/* Order number */}
            <span className="text-xs font-bold text-gray-400 dark:text-gray-500 w-6 text-center shrink-0">
              {index + 1}
            </span>

            {/* Product info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate group-hover:text-vivatis-600 dark:group-hover:text-vivatis-400 transition-colors">
                {product.name}
              </h3>
              <div className="flex items-center gap-2 mt-0.5">
                <span className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                  {product.articleNumber}
                </span>
              </div>
            </div>

            {/* Remove button */}
            <button
              type="button"
              title={lockedNotice ?? strings.cart.remove}
              disabled={isLocked}
              className="p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors shrink-0 cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:text-gray-400 disabled:hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation();
                removeFromCart(product.id);
              }}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>

      {/* Summary bar */}
      <div className="sticky bottom-0 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-xl shadow-lg p-4 -mx-4 sm:mx-0">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            {strings.cart.productCount(cartProducts.length)}
          </span>

          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="primary"
              size="sm"
              icon={documentPdf.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              disabled={documentPdf.isPending}
              onClick={() => {
                documentPdf.mutate(
                  { products: cartProducts, title: activeCart?.name, strings, contactPerson: user?.name, companyName: user?.branch_office?.name, companyWebsite: user?.branch_office?.website ?? undefined },
                  {
                    onSuccess: () => showToast(strings.cart.toastPdf),
                    onError: (err) => {
                      console.error('PDF generation failed:', err);
                      showToast(strings.cart.toastPdfError);
                    },
                  },
                );
              }}
            >
              {documentPdf.isPending ? strings.cart.generatingPdf : strings.cart.generatePdf}
            </Button>

            <Button
              variant="secondary"
              size="sm"
              icon={<Mail className="w-4 h-4" />}
              onClick={() => {
                if (!isOnline) {
                  toaster.show(strings.common.offlineTooltip, 'warning');
                  return;
                }
                setEmailOpen(true);
              }}
            >
              {strings.cart.sendViaEmail}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              icon={<CloudCog className="w-4 h-4" />}
              className="text-gray-400 dark:text-gray-500 opacity-60"
              onClick={() => setSfDialogOpen(true)}
            >
              {strings.cart.createOpportunity}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              icon={<Trash2 className="w-4 h-4" />}
              className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
              disabled={isLocked}
              onClick={() => setConfirmDialog({ action: 'clear' })}
            >
              {strings.cart.clearCart}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              icon={<Archive className="w-4 h-4" />}
              className="text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20"
              onClick={() => setConfirmDialog({ action: 'archive', cartId: activeCartId ?? undefined, cartName: activeCart?.name })}
            >
              {strings.cart.archiveDocument}
            </Button>

            {carts.length > 1 && (
              <Button
                variant="ghost"
                size="sm"
                icon={<Trash2 className="w-4 h-4" />}
                className="text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20"
                disabled={isLocked}
                onClick={() => setConfirmDialog({ action: 'delete', cartId: activeCartId ?? undefined, cartName: activeCart?.name })}
              >
                {strings.cart.deleteDocument}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* SalesForce coming-soon dialog */}
      {sfDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setSfDialogOpen(false)}
          />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 p-6 text-center">
            <div className="w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mx-auto mb-4">
              <Info className="w-6 h-6 text-gray-400 dark:text-gray-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {strings.cart.comingSoon}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              {strings.cart.comingSoonDescription}
            </p>
            <Button variant="primary" onClick={() => setSfDialogOpen(false)}>
              OK
            </Button>
          </div>
        </div>
      )}

      {/* Email dialog */}
      <EmailDialog
        isOpen={emailOpen}
        onClose={() => setEmailOpen(false)}
        onSend={handleSendEmail}
        isSending={cartEmail.isPending}
        defaultSubject={strings.cart.emailSubject(cartProducts.length, user?.branch_office?.name ?? 'Vivatis Pharma')}
        defaultBody={strings.cart.emailBody(formatProductList(cartProducts), user?.name ?? '', user?.branch_office?.name ?? 'Vivatis Pharma GmbH')}
        error={emailError}
        products={cartProducts}
        getSubject={(selected) => strings.cart.emailSubject(selected.length, user?.branch_office?.name ?? 'Vivatis Pharma')}
        getBody={(selected) => strings.cart.emailBody(formatProductList(selected as CartProduct[]), user?.name ?? '', user?.branch_office?.name ?? 'Vivatis Pharma GmbH')}
        availableFileTypes={cartFileTypes.data?.types}
        isLoadingFileTypes={cartFileTypes.isLoading}
      />

      {/* Confirm dialog for destructive cart actions */}
      <ConfirmDialog
        isOpen={confirmDialog !== null}
        onConfirm={handleConfirmAction}
        onCancel={() => setConfirmDialog(null)}
        title={
          confirmDialog?.action === 'archive'
            ? strings.cart.archiveDocument
            : confirmDialog?.action === 'clear'
              ? strings.cart.clearCart
              : strings.cart.deleteDocument
        }
        message={
          confirmDialog?.action === 'archive'
            ? strings.cart.confirmArchive(confirmDialog?.cartName ?? '')
            : confirmDialog?.action === 'clear'
              ? strings.cart.confirmClear
              : strings.cart.confirmDelete(confirmDialog?.cartName ?? '')
        }
        confirmLabel={
          confirmDialog?.action === 'archive'
            ? strings.cart.archiveDocument
            : confirmDialog?.action === 'clear'
              ? strings.cart.clearCart
              : strings.cart.deleteDocument
        }
        variant={confirmDialog?.action === 'archive' ? 'warning' : 'danger'}
        icon={
          confirmDialog?.action === 'archive'
            ? <Archive className="w-6 h-6" />
            : <Trash2 className="w-6 h-6" />
        }
      />
    </div>
  );
};

export default CartPage;
